import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useAppDispatch } from '@store';
import { Currency, useQueryState } from '@ui/src';
import { GeneralInfo } from '../../../individual/types';
import { CorporateGeneralInfo } from '../../../corporate/types';
import { RateSource, OrganizationType, RiskScoreRange } from '../../types';
import {
  useAssignTariffToCustomerMutation,
  useDeleteCustomFeeMutation,
  useLazyGetCustomersRateSourcesQuery,
  useLazyGetCustomersTariffsListQuery,
} from '../../../customers/store/services/customers';
import { FeeItem, Tariff, TariffFee } from '../types';
import { selectFees } from '../../store/slices/tariff/tariff';
import formatFeesList from '../helpers/format-fees-list';
import { feesSchema } from '../variables/fees';

const useTariff = ({
  type,
  riskScoreRangesDictionaries,
  tariffInfo,
  fees,
  customerId,
  tariffsList,
  rateSources,
  baseCurrency,
  currenciesList,
}: {
  type: OrganizationType;
  riskScoreRangesDictionaries?: RiskScoreRange[];
  tariffInfo?: Partial<GeneralInfo> | Partial<CorporateGeneralInfo>;
  fees: TariffFee;
  customerId?: string;
  tariffsList?: Tariff[];
  rateSources?: RateSource[];
  baseCurrency?: Currency;
  currenciesList?: Currency[];
}) => {
  const [selectedTariffId, setSelectedTariffId] = useState('');
  const [allMarginsVisible, setAllMarginsVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [showMoreCurrencies, setShowMoreCurrencies] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [menuOnFocus, setMenuOnFocus] = useState(false);
  const [selectedMarginId, setSeletedMarginId] = useState('');
  const [marginPanelIsOpen, setMarginPanelIsOpen] = useState(false);

  const [getRateSource] = useLazyGetCustomersRateSourcesQuery();
  const [
    assignTariffToCustomer,
    { isLoading: tariffChangeInProgress, isSuccess: tariffChangedSuccess },
  ] = useAssignTariffToCustomerMutation();
  const [
    deleteFee,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteCustomFeeMutation();
  const [getTariffsList, { isLoading }] = useLazyGetCustomersTariffsListQuery();

  const dispatch = useAppDispatch();

  const { sort } = useQueryState(feesSchema);

  const riskLevel = useMemo(
    () =>
      riskScoreRangesDictionaries?.find(
        (item) => tariffInfo?.tariffRiskScoreId === item.id,
      )?.name,
    [tariffInfo?.risk, riskScoreRangesDictionaries],
  );

  const formattedData = useMemo(() => {
    const data = formatFeesList(
      tariffInfo?.fees as FeeItem[],
      customerId,
      type,
      baseCurrency,
      currenciesList,
    );

    return data;
  }, [tariffInfo?.fees, baseCurrency, currenciesList]);

  const onDelete = () => {
    const customFees: string[] = [];

    fees.selectedFees.forEach((item) => {
      const currentFee = tariffInfo?.fees?.find((fee) => fee.id === item);

      if (currentFee && currentFee.isModified) {
        customFees.push(currentFee.id);
      }
    });

    customFees.forEach((item) => {
      deleteFee({
        id: customerId as string,
        feeId: item,
      });
    });
  };

  const assignTariffToCustomerAction = () => {
    assignTariffToCustomer({
      body: { tariffId: selectedTariffId },
      id: customerId as string,
    });
  };

  const getTariffsListAction = useCallback(() => {
    getTariffsList(`&type=${type}`);
  }, []);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(rows, fees.selectedFees)) {
      dispatch(selectFees(rows));
    }
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
  }, []);

  useEffect(() => {
    if (!tariffsList) {
      getTariffsListAction();
    }
  }, [tariffsList]);

  useEffect(() => {
    if (tariffChangedSuccess) {
      handleClose();
    }
  }, [tariffChangedSuccess]);

  useEffect(() => {
    if (!rateSources) {
      getRateSource();
    }
  }, [rateSources]);

  return {
    isLoading,
    setSelectedTariffId,
    selectedTariffId,
    panelIsOpen,
    handleClose,
    tariffChangeInProgress,
    assignTariffToCustomerAction,
    allMarginsVisible,
    formattedData,
    sort,
    onRowsSelect,
    riskLevel,
    deleteInProgress,
    onDelete,
    deleteIsSuccess,
    setPanelIsOpen,
    setAllMarginsVisible,
    setSelectedCurrency,
    selectedCurrency,
    showMoreCurrencies,
    setShowMoreCurrencies,
    setMenuOnFocus,
    menuOnFocus,
    setSeletedMarginId,
    setMarginPanelIsOpen,
    selectedMarginId,
    marginPanelIsOpen,
  };
};

export default useTariff;
